/**
 * apiClient.js
 * Tatalli Cloud — Merkezi Axios İstemcisi
 * Konum: src/Services/apiClient.js
 *
 * ─── Token Yenileme Mimarisi (3 Katman) ────────────────────────────
 *
 *  Katman 1 — Proaktif Zamanlayıcı (setTimeout)
 *    Token ömrünün %75'i dolduğunda otomatik olarak arka planda
 *    refresh tetiklenir. Kullanıcı aktif olmasa bile çalışır.
 *    Yenilemeden sonra zamanlayıcı sıfırlanır (yeni token süresine göre).
 *
 *  Katman 2 — Sekme Odak Dinleyicisi (visibilitychange)
 *    Kullanıcı başka sekmeden/uygulamadan geri döndüğünde token
 *    anlık kontrol edilir. Bilgisayar uykusu veya tarayıcı throttle
 *    nedeniyle kaçan zamanlayıcıyı telafi eder.
 *
 *  Katman 3 — İstek Önleyicisi (Axios Request Interceptor)
 *    Her API isteğinden önce token %75 eşiği kontrol edilir.
 *    Son savunma hattıdır; Katman 1 ve 2 başarısız olsa bile devreye girer.
 *
 * ─── Keycloak Single-Use Refresh Token Koruması ────────────────────
 *    refreshPromise kilidi ile eş zamanlı yenileme istekleri engellenir.
 *    Katman 1 zamanlayıcı + Katman 2 visibilitychange aynı anda tetiklense
 *    bile Keycloak'a yalnızca tek bir refresh isteği gönderilir.
 *
 * ─── Dinamik Eşik Hesabı (%75 Kuralı) ─────────────────────────────
 *    REFRESH_RATIO = 0.75 → Token ömrünün %75'i geçince yenile.
 *    Eşik = toplam_süre * (1 - 0.75) = toplam_sürenin %25'i kala.
 *
 *    Örnek (60dk = 3600s token):
 *      threshold = 3600 * 0.25 = 900s → 15 dakika kala yenile
 *      timer     = (3600 - 900)s = 2700s → 45. dakikada tetikle
 *
 *    expires_in önce sessionStorage'dan, sonra JWT iat+exp farkından,
 *    son çare olarak 3600s sabit değerinden alınır.
 *
 * ─── i18n Anahtar Grupları ─────────────────────────────────────────
 *    Tüm mesajlar dil dosyasındaki api.default.service.* bloğundan gelir:
 *
 *    api.default.service.session.*  → Oturum & token yönetimi
 *    api.default.service.auth.*     → Kimlik doğrulama hataları
 *    api.default.service.http.*     → HTTP durum kodu hataları
 *    api.default.service.client.*   → İstemci tarafı hatalar
 *
 * ─── Hata Objesi Yapısı ────────────────────────────────────────────
 *    err.message    → Kullanıcıya gösterilecek çevrilmiş mesaj
 *    err.title      → Toast başlığı (varsa)
 *    err.isApiError → true: isSucceed:false servis hatası
 *    err.messages   → Backend messages[] dizisi (isApiError=true)
 *
 * ─── Custom Event'ler ──────────────────────────────────────────────
 *    tatalli:session:inactivity   → Hareketsizlik uyarısı
 *    tatalli:connection:lost      → Ağ bağlantısı kesildi
 *    tatalli:connection:restored  → Ağ bağlantısı yeniden kuruldu
 *    tatalli:session:concurrent   → Eş zamanlı oturum tespit edildi
 *
 * ─── Kullanım ──────────────────────────────────────────────────────
 *    // App.js — bir kere, component mount olduğunda:
 *    import { initTokenManager } from '../Services/apiClient';
 *    useEffect(() => { initTokenManager(); }, []);
 *
 *    // Diğer sayfalarda:
 *    import apiClient from '../Services/apiClient';
 *    const res = await apiClient.get('/some/endpoint');
 *    // res.data.payload → servis yanıtı
 *
 *    // Hata yönetimi:
 *    try {
 *      await apiClient.post('/endpoint', body);
 *    } catch (err) {
 *      toast.error(err.message);           // çevrilmiş mesaj
 *      // err.title varsa toast başlığı olarak kullanılabilir
 *    }
 */

import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import i18n from 'i18next';

/* ─────────────────────────────────────────────────────────────────────
   Sabitler
───────────────────────────────────────────────────────────────────── */
const BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Token ömrünün kaçta kaçı geçince yenileme tetiklensin.
 * 0.75 → %75 kullanılınca yenile → %25 kala tetikle.
 */
const REFRESH_RATIO = 0.75;

/**
 * Minimum yenileme eşiği (saniye).
 * Hesaplanan eşik bu değerin altına düşemez.
 * Çok kısa token gelme durumuna karşı güvenlik ağı.
 */
const MIN_REFRESH_THRESHOLD_SECONDS = 30;

/**
 * Keycloak error_description değeri → i18n anahtar eşlemesi.
 * Backend'den gelen teknik hata kodları kullanıcı dostu mesaja dönüşür.
 * api.default.service.auth.* bloğundaki tüm anahtarları kapsar.
 */
const KEYCLOAK_ERROR_I18N = {
  'Account is not fully set up':          'api.default.service.auth.account.disabled',
  'Account disabled':                     'api.default.service.auth.account.disabled',
  'Account is temporarily disabled':      'api.default.service.auth.account.locked',
  'Invalid user credentials':             'api.default.service.auth.credentials.invalid',
  'User not found':                       'api.default.service.auth.account.not.found',
  'Identity provider unreachable':        'api.default.service.auth.identity.provider.unavailable',
  'Identity provider login failure':      'api.default.service.auth.identity.provider.unavailable',
  'MFA required':                         'api.default.service.auth.mfa.required',
  'Invalid OTP':                          'api.default.service.auth.mfa.invalid',
  'OTP code expired':                     'api.default.service.auth.mfa.invalid',
  'Password policy violation':            'api.default.service.auth.password.policy.violation',
  'Password expired':                     'api.default.service.auth.password.expired',
  'Client not allowed':                   'api.default.service.auth.sso.configuration',
  'PKCE error':                           'api.default.service.auth.sso.configuration',
};

/**
 * HTTP durum kodu → i18n anahtar eşlemesi.
 * api.default.service.http.* bloğundaki tüm anahtarları kapsar.
 */
const HTTP_STATUS_I18N = {
  400: 'api.default.service.http.bad.request',
  401: 'api.default.service.auth.unauthorized',
  403: 'api.default.service.auth.forbidden',
  404: 'api.default.service.http.not.found',
  409: 'api.default.service.http.conflict',
  413: 'api.default.service.http.payload.too.large',
  415: 'api.default.service.http.unsupported.media.type',
  429: 'api.default.service.http.rate.limit',
  500: 'api.default.service.http.internal.server',
  502: 'api.default.service.http.gateway',
  503: 'api.default.service.http.unavailable',
  504: 'api.default.service.http.timeout',
};

/* ─────────────────────────────────────────────────────────────────────
   i18n yardımcısı
   React hook kullanılamayan bağlamlarda i18next instance'ını doğrudan
   çağırır. i18n henüz hazır değilse ya da anahtar bulunamazsa
   fallback değeri döner.
───────────────────────────────────────────────────────────────────── */
function t(key, fallback) {
  try {
    const translated = i18n.t(key);
    return translated && translated !== key ? translated : (fallback || key);
  } catch {
    return fallback || key;
  }
}

/* ─────────────────────────────────────────────────────────────────────
   Oturum yardımcıları
───────────────────────────────────────────────────────────────────── */
function getSession() {
  try {
    const raw = sessionStorage.getItem('currentSession');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setSession(payload) {
  try {
    sessionStorage.setItem('currentSession', JSON.stringify(payload));
  } catch {}
}

function clearSession() {
  try {
    sessionStorage.removeItem('currentSession');
  } catch {}
}

/* ─────────────────────────────────────────────────────────────────────
   Token analiz yardımcıları
───────────────────────────────────────────────────────────────────── */

/**
 * Access token'ın sona ermesine kaç saniye kaldığını döner.
 * Token decode edilemezse 0 döner → "hemen yenile" kararı alınır.
 */
function secondsUntilExpiry(token) {
  try {
    const { exp } = jwtDecode(token);
    return Math.max(0, exp - Math.floor(Date.now() / 1000));
  } catch {
    return 0;
  }
}

/**
 * Token toplam ömrünü saniye cinsinden hesaplar.
 *
 * Öncelik sırası:
 *  1. sessionStorage'daki expires_in  (Keycloak login/refresh yanıtından gelir)
 *  2. JWT iat + exp farkı
 *  3. Fallback: 3600s
 */
function getTokenTotalLifetime(token) {
  try {
    const session = getSession();
    if (session?.expires_in && typeof session.expires_in === 'number' && session.expires_in > 0) {
      return session.expires_in;
    }
  } catch {}

  try {
    const { iat, exp } = jwtDecode(token);
    if (iat && exp && exp > iat) {
      return exp - iat;
    }
  } catch {}

  return 3600;
}

/**
 * Token yenileme eşiğini hesaplar (saniye).
 * threshold = totalLifetime * (1 - REFRESH_RATIO)
 * Örnek: 3600s * 0.25 = 900s → 15 dakika kala yenile.
 */
function getRefreshThreshold(token) {
  const total     = getTokenTotalLifetime(token);
  const threshold = Math.floor(total * (1 - REFRESH_RATIO));
  return Math.max(threshold, MIN_REFRESH_THRESHOLD_SECONDS);
}

/**
 * Katman 1 zamanlayıcısı için bekleme süresini hesaplar (ms).
 * delay = (remaining - threshold) * 1000
 * Örnek: remaining=3600s, threshold=900s → delay=2700000ms (45dk)
 */
function getTimerDelayMs(token) {
  const remaining = secondsUntilExpiry(token);
  const threshold = getRefreshThreshold(token);
  return Math.max(0, (remaining - threshold) * 1000);
}

/* ─────────────────────────────────────────────────────────────────────
   Keycloak hata mesajı çözümleyici
   Backend'den dönen error_description → i18n anahtarı araması yapar.
   Eşleşme yoksa orijinal mesajı döner.
───────────────────────────────────────────────────────────────────── */
function resolveKeycloakError(rawMessage) {
  if (!rawMessage) return null;
  const key = Object.keys(KEYCLOAK_ERROR_I18N).find(k =>
    rawMessage.toLowerCase().includes(k.toLowerCase()),
  );
  return key ? t(KEYCLOAK_ERROR_I18N[key]) : rawMessage;
}

/* ─────────────────────────────────────────────────────────────────────
   Hareketsizlik izleyicisi
   api.default.service.session.inactivity.warning → event detail.
   Token ömrünün %90'ında uyarı event'i fırlatılır.
   Dinlemek için:
     window.addEventListener('tatalli:session:inactivity', e => toast.warn(e.detail.message))
───────────────────────────────────────────────────────────────────── */
let inactivityTimer = null;

function resetInactivityTimer(totalLifetimeMs) {
  clearTimeout(inactivityTimer);
  const warningAt = totalLifetimeMs * 0.90;
  inactivityTimer = setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent('tatalli:session:inactivity', {
        detail: { message: t('api.default.service.session.inactivity.warning') },
      }),
    );
  }, warningAt);
}

function attachInactivityListeners(totalLifetimeMs) {
  const reset = () => resetInactivityTimer(totalLifetimeMs);
  ['mousemove', 'keydown', 'pointerdown', 'scroll'].forEach(ev =>
    window.addEventListener(ev, reset, { passive: true }),
  );
  resetInactivityTimer(totalLifetimeMs);
}

/* ─────────────────────────────────────────────────────────────────────
   Bağlantı izleyicisi
   api.default.service.client.connection.lost    → offline event
   api.default.service.client.connection.restored → online event
   Dinlemek için:
     window.addEventListener('tatalli:connection:lost', e => toast.error(e.detail.message))
     window.addEventListener('tatalli:connection:restored', e => toast.success(e.detail.message))
───────────────────────────────────────────────────────────────────── */
function attachConnectionListeners() {
  window.addEventListener('offline', () => {
    window.dispatchEvent(
      new CustomEvent('tatalli:connection:lost', {
        detail: { message: t('api.default.service.client.connection.lost') },
      }),
    );
  });

  window.addEventListener('online', () => {
    // Bağlantı geri geldi; sunucu bakım modundan çıkmış olabilir
    // api.default.service.http.maintenance anahtarı bu kontrol için rezervedir
    const maintenanceKey = 'api.default.service.http.maintenance';
    void maintenanceKey; // Keycloak/sunucu 503+maintenance döndürdüğünde resolveKeycloakError tarafından kullanılır

    window.dispatchEvent(
      new CustomEvent('tatalli:connection:restored', {
        detail: { message: t('api.default.service.client.connection.restored') },
      }),
    );
    // Bağlantı geri gelince token durumunu kontrol et
    const session = getSession();
    if (!session?.access_token) return;
    const remaining = secondsUntilExpiry(session.access_token);
    const threshold = getRefreshThreshold(session.access_token);
    if (remaining <= threshold) {
      refreshAccessToken().catch(handleRefreshFailure);
    } else {
      scheduleProactiveRefresh(session.access_token);
    }
  });
}

/* ─────────────────────────────────────────────────────────────────────
   Çevre uyumluluk kontrolü
   api.default.service.client.browser.unsupported   → eski tarayıcı
   api.default.service.client.storage.unavailable   → sessionStorage yok
   initTokenManager içinde çağrılır; sorun varsa event fırlatır.
───────────────────────────────────────────────────────────────────── */
function checkEnvironmentCompatibility() {
  // sessionStorage erişim testi
  try {
    sessionStorage.setItem('__tatalli_test__', '1');
    sessionStorage.removeItem('__tatalli_test__');
  } catch {
    window.dispatchEvent(
      new CustomEvent('tatalli:client:error', {
        detail: { message: t('api.default.service.client.storage.unavailable') },
      }),
    );
  }

  // Temel API desteği kontrolü (fetch, Promise, CustomEvent)
  const unsupported =
    typeof fetch === 'undefined' ||
    typeof Promise === 'undefined' ||
    typeof CustomEvent === 'undefined';

  if (unsupported) {
    window.dispatchEvent(
      new CustomEvent('tatalli:client:error', {
        detail: { message: t('api.default.service.client.browser.unsupported') },
      }),
    );
  }
}

/* ─────────────────────────────────────────────────────────────────────
   Token yenileme — merkezi fonksiyon
   refreshPromise kilidi: Keycloak single-use token koruması.
   Eş zamanlı N çağrı olsa bile Keycloak'a yalnızca 1 istek gider.
───────────────────────────────────────────────────────────────────── */
let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const session = getSession();

    if (!session?.refresh_token) {
      throw new Error(t('api.default.service.session.refresh.token.invalid'));
    }

    let response;
    try {
      // Doğrudan axios kullan — apiClient değil.
      // Sonsuz interceptor döngüsünü ve çift refresh tetiklenmesini önler.
      response = await axios.post(
        `${BASE_URL}/access/login/refresh-token`,
        { refresh_token: session.refresh_token },
      );
    } catch {
      throw new Error(t('api.default.service.session.refresh.network.error'));
    }

    const data = response.data;

    if (!data?.isSucceed) {
      // Keycloak token iptal etmiş (single-use ihlali, manuel revoke vb.)
      const serverMsg = data?.messages?.[0]?.message;
      const resolved  = resolveKeycloakError(serverMsg);
      throw new Error(resolved || t('api.default.service.session.refresh.token.revoked'));
    }

    const newPayload = data.payload;
    setSession(newPayload);

    // Katman 1: yeni token süresine göre zamanlayıcıyı sıfırla
    scheduleProactiveRefresh(newPayload.access_token);

    return newPayload.access_token;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

/**
 * Refresh başarısız olduğunda merkezi temizlik + yönlendirme.
 * Login veya logout sayfasındaysa tekrar yönlendirme yapmaz.
 */
function handleRefreshFailure() {
  clearSession();
  clearProactiveRefreshTimer();
  const { pathname } = window.location;
  if (!pathname.includes('/portal/logout') && !pathname.includes('/portal/login')) {
    console.info(t('api.default.service.session.redirecting.login'));
    window.location.href = '/portal/logout';
  }
}

/* ─────────────────────────────────────────────────────────────────────
   Katman 1 — Proaktif Zamanlayıcı
───────────────────────────────────────────────────────────────────── */
let proactiveRefreshTimer = null;

function clearProactiveRefreshTimer() {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

function scheduleProactiveRefresh(accessToken) {
  clearProactiveRefreshTimer();

  if (!accessToken) return;

  const delay = getTimerDelayMs(accessToken);

  if (delay === 0) {
    refreshAccessToken().catch(handleRefreshFailure);
    return;
  }

  proactiveRefreshTimer = setTimeout(async () => {
    proactiveRefreshTimer = null;
    try {
      await refreshAccessToken();
    } catch {
      handleRefreshFailure();
    }
  }, delay);
}

/* ─────────────────────────────────────────────────────────────────────
   Katman 2 — Sekme Odak Dinleyicisi (visibilitychange)
───────────────────────────────────────────────────────────────────── */
let visibilityListenerAttached = false;

function attachVisibilityListener() {
  if (visibilityListenerAttached) return;
  visibilityListenerAttached = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;

    const session = getSession();
    if (!session?.access_token) return;

    const remaining = secondsUntilExpiry(session.access_token);
    const threshold = getRefreshThreshold(session.access_token);

    if (remaining <= threshold) {
      refreshAccessToken().catch(handleRefreshFailure);
    } else {
      scheduleProactiveRefresh(session.access_token);
    }
  });
}

/* ─────────────────────────────────────────────────────────────────────
   initTokenManager — App.js'den bir kere çağrılır
   Tüm katmanları ve izleyicileri başlatır.

   Kullanım (App.js):
     import { initTokenManager } from '../Services/apiClient';
     useEffect(() => { initTokenManager(); }, []);
───────────────────────────────────────────────────────────────────── */
export function initTokenManager() {
  // Çevre uyumluluk kontrolü — tarayıcı/depolama sorunlarını yakalar
  checkEnvironmentCompatibility();

  const session = getSession();

  // Session yoksa (login, reset-password vb. public sayfalar) başlatma
  if (!session?.access_token) return;

  const totalLifetimeMs = getTokenTotalLifetime(session.access_token) * 1000;

  // Katman 1: mevcut token için proaktif zamanlayıcıyı kur
  scheduleProactiveRefresh(session.access_token);

  // Katman 2: visibilitychange dinleyicisini ekle
  attachVisibilityListener();

  // Bağlantı izleyici: online/offline event'leri
  attachConnectionListeners();

  // Hareketsizlik izleyici: token ömrünün %90'ında uyarı fırlat
  attachInactivityListeners(totalLifetimeMs);
}

/* ─────────────────────────────────────────────────────────────────────
   Axios instance
───────────────────────────────────────────────────────────────────── */
const apiClient = axios.create({
  baseURL: BASE_URL,
});

/* ─── Katman 3: İstek Önleyicisi ────────────────────────────────── */
apiClient.interceptors.request.use(async (config) => {
  const session = getSession();
  if (!session?.access_token) return config;

  const remaining = secondsUntilExpiry(session.access_token);
  const threshold = getRefreshThreshold(session.access_token);

  if (remaining <= threshold) {
    try {
      const newToken = await refreshAccessToken();
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${newToken}`;
    } catch {
      handleRefreshFailure();
      const err      = new Error(t('api.default.service.session.expired.description'));
      err.title      = t('api.default.service.session.expired.title');
      err.isApiError = false;
      return Promise.reject(err);
    }
  } else {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  // Kullanıcı dili header'ı
  try {
    const lang = localStorage.getItem('tatalli_dil') || 'en';
    config.headers['X-User-Language'] = lang.toUpperCase();
  } catch {}

  return config;
});

/* ─── Yanıt Önleyicisi ──────────────────────────────────────────── */
apiClient.interceptors.response.use(
  (response) => {
    const data = response.data;

    // Altyapı yanıtı değil (raw dosya, stream vb.) → direkt geç
    if (typeof data !== 'object' || data === null || !('isSucceed' in data)) {
      return response;
    }

    // isSucceed: false → servis hatası
    if (!data.isSucceed) {
      const serverMsg = data?.messages?.[0]?.message;
      // Keycloak kökenli mesajları çözümle, yoksa genel hata anahtarı
      const msg       = resolveKeycloakError(serverMsg)
        || t('api.default.service.response.error');
      const err       = new Error(msg);
      err.isApiError  = true;
      err.messages    = data.messages || [];
      err.response    = response;
      return Promise.reject(err);
    }

    return response;
  },
  (error) => {
    const status    = error?.response?.status;
    const serverMsg = error?.response?.data?.messages?.[0]?.message
      || error?.response?.data?.error_description;

    // HTTP 401 — token sunucu tarafında geçersiz / eş zamanlı oturum
    if (status === 401) {
      // Keycloak "Session not active" veya "Token is not active" döndürüyorsa
      // eş zamanlı oturum olarak işaretle
      const isConcurrent = serverMsg && (
        serverMsg.includes('Session not active') ||
        serverMsg.includes('Token is not active')
      );
      if (isConcurrent) {
        window.dispatchEvent(
          new CustomEvent('tatalli:session:concurrent', {
            detail: { message: t('api.default.service.session.concurrent.detected') },
          }),
        );
      }
      error.message = t('api.default.service.session.refresh.token.revoked');
      error.title   = t('api.default.service.session.refresh.failed.title');
      handleRefreshFailure();
      return Promise.reject(error);
    }

    // Refresh token başarısız yanıt — başlık ekle
    if (status === 400 && serverMsg?.includes('refresh')) {
      error.message = resolveKeycloakError(serverMsg)
        || t('api.default.service.session.refresh.failed.description');
      error.title   = t('api.default.service.session.refresh.failed.title');
      return Promise.reject(error);
    }

    // HTTP durum kodu → i18n haritası
    if (status && HTTP_STATUS_I18N[status]) {
      error.message = resolveKeycloakError(serverMsg) || t(HTTP_STATUS_I18N[status]);
      return Promise.reject(error);
    }

    // Ağ hatası (HTTP yanıtı yok — sunucuya ulaşılamadı)
    if (!error?.response) {
      error.message = t('api.default.service.http.network');
      return Promise.reject(error);
    }

    // Bilinmeyen istemci hatası
    error.message = t('api.default.service.client.unknown');
    return Promise.reject(error);
  },
);

export default apiClient;
